import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const statusConfig = {
  Pending: { color: 'bg-warning/10 text-warning', icon: 'Clock' },
  Approved: { color: 'bg-success/10 text-success', icon: 'CheckCircle' },
  Denied: { color: 'bg-destructive/10 text-destructive', icon: 'XCircle' },
  Cancelled: { color: 'bg-muted text-muted-foreground', icon: 'Ban' },
};

const typeConfig = {
  Vacation: { color: 'bg-primary/10 text-primary', icon: 'Palmtree' },
  Sick: { color: 'bg-warning/10 text-warning', icon: 'Thermometer' },
  Personal: { color: 'bg-accent/10 text-accent', icon: 'User' },
  Bereavement: { color: 'bg-muted text-muted-foreground', icon: 'Heart' },
  Other: { color: 'bg-secondary/10 text-secondary', icon: 'MoreHorizontal' },
};

const PTORequestsTable = ({ requests, employees, onApprove, onDeny, onBulkApprove, onDelete, loading }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [denyingId, setDenyingId] = useState(null);
  const [denyReason, setDenyReason] = useState('');

  const getEmployee = (employeeId) =>
    employees?.find((e) => e?.id === employeeId);

  const toggleExpand = (id) =>
    setExpandedRow(expandedRow === id ? null : id);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const pendingIds = requests?.filter((r) => r?.status === 'Pending')?.map((r) => r?.id) ?? [];
    if (selectedIds?.length === pendingIds?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingIds);
    }
  };

  const handleDenySubmit = (id) => {
    onDeny?.(id, denyReason);
    setDenyingId(null);
    setDenyReason('');
  };

  const pendingCount = requests?.filter((r) => r?.status === 'Pending')?.length ?? 0;

  if (loading) {
    return (
      <div className="bg-card rounded-xl elevation-sm p-8 text-center">
        <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Loading requests...</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl elevation-sm overflow-hidden">
      <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-heading font-semibold text-foreground">PTO Requests</h3>
          <span className="text-xs font-caption bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
            {requests?.length ?? 0} total
          </span>
          {pendingCount > 0 && (
            <span className="text-xs font-caption bg-warning/10 text-warning px-2 py-0.5 rounded-full">
              {pendingCount} pending
            </span>
          )}
        </div>
        {selectedIds?.length > 0 && (
          <Button
            size="sm"
            variant="default"
            iconName="CheckCheck"
            onClick={() => { onBulkApprove?.(selectedIds); setSelectedIds([]); }}
          >
            Approve Selected ({selectedIds?.length})
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-4 py-3 w-8">
                <input
                  type="checkbox"
                  checked={selectedIds?.length === pendingCount && pendingCount > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Employee</th>
              <th className="text-left px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Type</th>
              <th className="text-left px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Dates</th>
              <th className="text-center px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Days</th>
              <th className="text-left px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Status</th>
              <th className="text-right px-4 py-3 text-xs font-caption font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests?.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-10">
                  <Icon name="CalendarOff" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No PTO requests found</p>
                </td>
              </tr>
            ) : (
              requests?.map((req) => {
                const emp = getEmployee(req?.employeeId);
                const statusCfg = statusConfig?.[req?.status] ?? statusConfig?.Pending;
                const typeCfg = typeConfig?.[req?.ptoType] ?? typeConfig?.Other;
                const isExpanded = expandedRow === req?.id;
                const isPending = req?.status === 'Pending';

                return (
                  <React.Fragment key={req?.id}>
                    <tr
                      className={`border-b border-border/50 hover:bg-muted/20 transition-colors cursor-pointer ${
                        isExpanded ? 'bg-muted/10' : ''
                      }`}
                      onClick={() => toggleExpand(req?.id)}
                    >
                      <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                        {isPending && (
                          <input
                            type="checkbox"
                            checked={selectedIds?.includes(req?.id)}
                            onChange={() => toggleSelect(req?.id)}
                            className="rounded border-border"
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-body font-medium text-foreground">{emp?.name ?? 'Unknown'}</p>
                          <p className="text-xs font-caption text-muted-foreground">{emp?.department ?? '—'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-caption font-medium ${typeCfg?.color}`}>
                          <Icon name={typeCfg?.icon} size={12} />
                          {req?.ptoType}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-body text-foreground">
                            {req?.startDate} → {req?.endDate}
                          </p>
                          <p className="text-xs font-caption text-muted-foreground">
                            Submitted {req?.createdAt ? new Date(req.createdAt)?.toLocaleDateString() : '—'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-data font-semibold text-foreground">{req?.daysRequested}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-caption font-medium ${statusCfg?.color}`}>
                          <Icon name={statusCfg?.icon} size={12} />
                          {req?.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e?.stopPropagation()}>
                        {isPending && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => onApprove?.(req?.id)}
                              className="p-1.5 rounded-lg bg-success/10 text-success hover:bg-success/20 transition-colors"
                              title="Approve"
                            >
                              <Icon name="Check" size={14} />
                            </button>
                            <button
                              onClick={() => setDenyingId(req?.id)}
                              className="p-1.5 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                              title="Deny"
                            >
                              <Icon name="X" size={14} />
                            </button>
                          </div>
                        )}
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <button
                            onClick={() => onDelete?.(req?.id)}
                            className="p-1.5 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                            title="Delete request"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                          <button className="p-1 text-muted-foreground hover:text-foreground">
                            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded Detail Row */}
                    {isExpanded && (
                      <tr className="border-b border-border/50 bg-muted/5">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-xs font-caption text-muted-foreground mb-1">Reason</p>
                              <p className="font-body text-foreground">{req?.reason ?? 'No reason provided'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-caption text-muted-foreground mb-1">Coverage Arrangement</p>
                              <p className="font-body text-foreground">{req?.coverageArrangement ?? 'Not specified'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-caption text-muted-foreground mb-1">Approval Info</p>
                              {req?.status === 'Approved' ? (
                                <p className="font-body text-foreground">
                                  Approved by {req?.approvedBy ?? 'Manager'} on {req?.approvedDate ?? '—'}
                                </p>
                              ) : req?.status === 'Denied' ? (
                                <p className="font-body text-destructive">{req?.denialReason ?? 'No reason given'}</p>
                              ) : (
                                <p className="font-body text-muted-foreground">Awaiting decision</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Deny Modal inline */}
                    {denyingId === req?.id && (
                      <tr className="border-b border-border/50 bg-destructive/5">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <p className="text-sm font-body font-medium text-foreground mb-2">Denial Reason</p>
                              <textarea
                                value={denyReason}
                                onChange={(e) => setDenyReason(e?.target?.value)}
                                placeholder="Enter reason for denial..."
                                rows={2}
                                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-destructive/30 text-foreground placeholder:text-muted-foreground resize-none"
                              />
                            </div>
                            <div className="flex gap-2 mt-7">
                              <Button size="sm" variant="destructive" onClick={() => handleDenySubmit(req?.id)}>Deny</Button>
                              <Button size="sm" variant="ghost" onClick={() => { setDenyingId(null); setDenyReason(''); }}>Cancel</Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PTORequestsTable;
